import FeatureReleaseNotificationListHeader from './FeatureReleaseNotificationListHeader';
import { useEffect, useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; 
import 'ag-grid-community/styles/ag-theme-alpine.css'; 
import * as StyledComponents from '../StyledComponents';
import { GridApi, ColumnApi, ExcelStyle } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AG_GRID_LICENSE } from '../../../../constants';
import { AdminTableBodySkeleton } from '../../Blocks/AdminTableBodySkeleton';
import moment from 'moment';
import { useUnifyAdminStore } from '../../AdminStore';
import {
  strToDate,
  formattedTime,
  getLocalizationValues
} from '../../utils';
import { DeleteConfirmDialog } from '../Elements/DeleteConfirmDialog';
import {useAllApplicationsStore, useAllWorkflowsStore} from '../../../../Store/index';
import { useGetAllNotifications,
   useRemoveQuery } from '../Store/useReleaseNotificationQuery';
import { Overlay } from './Overlay';
import {excelConfig, defaultColDef, getColumnDefs} from './FeatureReleaseGridConfig';
import { useUserProfile, useDepartmentList } from '../../../../hooks/queries';
import { useTranslation } from 'react-i18next';

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

export default function FeatureReleaseNotificationList(): JSX.Element {
  const { t } = useTranslation();
  const { data: departmentTypeList = [] } = useDepartmentList();
  const setDepartmentList = useUnifyAdminStore((state) => state.setDepartmentList);

  useEffect(() => {
    if (departmentTypeList.length > 0) {
      setDepartmentList(departmentTypeList);
    }
  }, [departmentTypeList, setDepartmentList]);

  const {data: userProfile} = useUserProfile();
  const setReleaseShowToastState = useUnifyAdminStore(
    (state) => state.setReleaseShowToastState
  );

  const setSideSheetData = useUnifyAdminStore(
    (state) => state.setReleaseSideSheetData
  );

  const sideSheetData = useUnifyAdminStore(
    (state) => state.releaseSideSheetData
  );

  const setFeatureReleaseSideSheetCollapse = useUnifyAdminStore(
    (state) => state.setReleaseSideSheetCollapse
  );
  const setReleaseNotifications = useUnifyAdminStore(
    (state) => state.setReleaseNotifications
  );

  const deleteDialogOpen = useUnifyAdminStore(
    (state) => state.releaseDeleteDialogOpen
  );
  const setDeleteDialogOpen = useUnifyAdminStore(
    (state) => state.setReleaseDeleteDialogOpen
  );
  const searchValue = useUnifyAdminStore((state) => state.releaseSearchValue);

  const releaseSideSheetData = useUnifyAdminStore(
    (state) => state.releaseSideSheetData
  );
  const releasePermission = useUnifyAdminStore(
    (state) => state.permissions
  );

  const setShowStartDateValidation = useUnifyAdminStore((state) => state.setShowStartDateValidation);
  const setShowEndDateValidation = useUnifyAdminStore((state) => state.setShowEndDateValidation);

  const {mutation:deleteMutation} = useRemoveQuery({
    onSuccess: () => {
      setReleaseShowToastState(t('featureRelease.toastDeleteSuccess', { title: getLocalizationValues(userProfile, sideSheetData).title }), 'Success');
    },
    onError: () => {
      setReleaseShowToastState(t('featureRelease.toastDeleteError'), 'Error');
    }});

  const deleteRelease = () =>{
    deleteMutation.mutate({messageId:sideSheetData.messageId});
  }

  const applications = useAllApplicationsStore((state:any) => state.applications);
  const workflows = useAllWorkflowsStore((state:any) => state.workflows);
  const archiveSwitch = useUnifyAdminStore((state) => state.releaseArchiveSwitch);

  const [gridApi, setGridApi] = useState<GridApi>();
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (gridApi) {
      gridApi.setQuickFilter(searchValue);
    }
  }, [searchValue, gridApi]);

  const { data: listOfFeatureReleaseNotification, isFetching } =
  useGetAllNotifications(archiveSwitch);

  useEffect(()=>{
    if(listOfFeatureReleaseNotification){
      setReleaseNotifications(listOfFeatureReleaseNotification);
    }
  },[listOfFeatureReleaseNotification,setReleaseNotifications])

  const onGridReady = (params: { api: GridApi; columnApi: ColumnApi }) => {
    setGridApi(params.api);
  };

  const cellClickedListener = useCallback((event: any) => {
    let data = { ...event.data };

    setShowStartDateValidation(false);
    setShowEndDateValidation(false);


    if(event.colDef.colId === 'Duplicate'){
      data.title = "Copy of ".concat(event.data.title);
      data.messageId = null;
    }

    if (event.data.caseId) {
      data.outageType = 'Unplanned';
    } else {
      data.outageType = 'Planned';
    }

    if (data.createdDate) {
      const dateTime = strToDate(data.createdDate);
      data.createdTime =  formattedTime(dateTime);
      data.createdDate = moment(dateTime);
    }

    if (data.startDate) {
      const dateTime = strToDate(data.startDate);
      data.startTime =  formattedTime(dateTime);
      data.startDate = moment(dateTime);
    }

    if (data.endDate) {
      const dateTime = strToDate(data.endDate);
      data.endTime = formattedTime(dateTime);
      data.endDate = moment(dateTime);
    }

    if (event.data.removed) {
      data.status = 'Closed';
    } else {
      data.status = 'Open';
    }

    data.targetApplications = applications && applications[0]
      .filter((app: any) => event.data.target.application?.includes(app.appId))
      .map((app: any) => ({ id: app.appId, label: app.name, value: app.appId }));

    if (workflows && workflows[0]) {
      data.targetWorkflows = workflows[0]
        .filter((workflow: any) =>
          event.data.target.workflow?.includes(workflow.workflowId)
        )
        .map((workflow: any) => ({
          id: workflow.workflowId,
          label: workflow.name,
          value: workflow.workflowId,
        }));
    }

    if(event.data.target.department && event.data.target.department[0] && departmentTypeList.length > 0){
      const targetDepartmentsValue = event.data.target.department?.map((deptId: any) => {
        const departmentOptions = departmentTypeList;
        const department = departmentOptions.find((option: any) => option.id === deptId);
        if (department) {
          return {
            id: department.id,
            label: department.label,
            value: department.value
          };
        }
        return null;
      });
      data.targetDepartments = targetDepartmentsValue.filter((d: any) => d !== null);
    } 

    data.targetEnterprises = event.data.target.enterpriseIds?.join(', ')||'';
    data.uploadedEnterprises = event.data.target.enterpriseIds?.join(', ')||'';
  
    data.targetCNumbers = event.data.target.cnumbers?.join(', ')||'';
    data.uploadedCNumbers = event.data.target.cnumbers?.join(', ')||'';

    data.targetStores = event.data.target.stores?.join(', ')||'';
    data.uploadedStores = event.data.target.stores?.join(', ')||'';

    data.linkToLearnMore = data.externalUrl;
    data.linkToLearnMoreLocale = data.externalUrlLocale;

    setSideSheetData(data);

    if (event.colDef.colId !== 'Delete') {
      setFeatureReleaseSideSheetCollapse(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentTypeList]);

  let columns = getColumnDefs(releasePermission, applications, workflows, archiveSwitch, t);

  return (
    <>
      <FeatureReleaseNotificationListHeader gridRef={gridRef} />
      {deleteDialogOpen && (
        <DeleteConfirmDialog
          title={getLocalizationValues(userProfile, releaseSideSheetData).title}
          subject={getLocalizationValues(userProfile, releaseSideSheetData).subject}
          isOpen={true}
          onYes={() => {
            deleteRelease();
            setDeleteDialogOpen(false);
          }}
          onNo={() => {
            setDeleteDialogOpen(false);
          }}
        ></DeleteConfirmDialog>
      )}
      <Overlay isOpen={deleteDialogOpen} panelId="panel-4"/>
      {isFetching ? (
        <AdminTableBodySkeleton />
      ) : (
        <StyledComponents.StyledTableContainer>
          <StyledComponents.StyledTableWrapper className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              rowData={listOfFeatureReleaseNotification}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              rowSelection="single"
              excludeHiddenColumnsFromQuickFilter={true}
              suppressDragLeaveHidesColumns={true}
              defaultExcelExportParams={excelConfig}
              excelStyles={excelConfig.excelStyles as ExcelStyle[]}
              onCellClicked={cellClickedListener}
            />
          </StyledComponents.StyledTableWrapper>
        </StyledComponents.StyledTableContainer>
      )}
    </>
  );
}
